import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Input from '../../../components/forms/Input'
import FilterInsertModal from '../../../components/modal/FilterInsertModal'
import { hasValue } from '../../../utils/NumberUtil'
import { Button, Card, CardContent, Grid, Icon } from '@mui/material'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import useBoolean from 'utils/customHook/useBoolean'
import AdministrationAction from 'administration/actions/AdministrationAction'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import { getTypeModule } from 'utils/StationUtils'
import DtoNetwork from 'station/dto/DtoNetwork'
import DtoStation from 'station/dto/DtoStation'
import { isUndefined, uniq } from 'lodash'
import DtoContributorLink from 'station/dto/DtoContributorLink'
import DtoPiezometerContributorLink from 'piezometry/dto/DtoPiezometerContributorLink'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { DEFAULT_CONTRIBUTOR_TYPE, FILTER_TYPE } from 'administration/components/user/constants/UserConstants'
import NetworkDto from 'referencial/components/network/dto/NetworkDto'
import DtoQualitometerListSpecific from 'quality/dto/DtoQualitometerListSpecific'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import moment from 'moment'

const StationFilterFields = ({
    defaultFilter = {},
    onValidate = () => { },
    stationType,
    stations,
    networklist = [],
    contributorLinks = [],
}) => {
    const dispatch = useDispatch()
    const { accountUser } = useSelector(store => store.AccountReducer, shallowEqual)
    const { contributorsIndex } = useSelector(store => store.ContributorReducer, shallowEqual)

    const MODULE = getTypeModule(stationType)
    const {
        value: isOpen,
        setTrue: openPopin,
        setValue: setOpen,
    } = useBoolean(false)

    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const now = moment().valueOf()
    const referentsList = uniq(contributorLinks.filter(c => c.contributorType === referentType && (c.endDate >= now || !c.endDate)).map(l => l.idContributor)).map(id => contributorsIndex[id]).filter(c => !isUndefined(c))
    const operatorsList = uniq(contributorLinks.filter(c => c.contributorType === operatorType && (c.endDate >= now || !c.endDate)).map(l => l.idContributor)).map(id => contributorsIndex[id]).filter(c => !isUndefined(c))

    const [searchValue, setSearchValue] = useState(defaultFilter.searchValue)
    const [referentIds, setReferentIds] = useState(defaultFilter.referentIds ?? [])
    const [operatorIds, setOperatorIds] = useState(defaultFilter.operatorIds ?? [])
    const [filter, setFilter] = useState(defaultFilter.filter)
    const [filterResults, setFilterResults] = useState([])
    const [filterParameters, setFilterParameters] = useState([])
    const [isValidateOpen, setIsOpenValidate] = useState()
    const [network, setNetwork] = useState(defaultFilter.network)

    const onLaunchSearch = (value) => {
        dispatch(AdministrationAction.setCache(stationType, { searchValue: value || searchValue, referentIds, operatorIds, network }))
        dispatch(AdministrationAction.setSelectedSearchValues(stationType, { searchValue: value || searchValue, referentIds, operatorIds, network }))

        if (filterParameters?.length) {
            setIsOpenValidate(true)
        } else {
            onValidate({
                searchValue: value || searchValue,
                filter,
                filterResults,
                network,
                referentIds,
                operatorIds,
            })
        }
    }

    return (
        <Card>
            <CardContent>
                <Grid container alignItems='end' justifyContent='space-between'>
                    <Grid container size={12} spacing={1}>
                        <Grid size={6}>
                            <Input
                                tooltip={i18n.searchThenEnter}
                                title={i18n.search}
                                value={searchValue}
                                onChange={setSearchValue}
                                onEnterKeyPress={(value, clearFunc) => {
                                    setSearchValue(value)
                                    if (clearFunc !== 'clearFunc') {
                                        onLaunchSearch(value)
                                    }
                                }}
                                clearFunction
                            />
                        </Grid>
                        <Grid container size={6} alignItems='center'>
                            <Grid size={11}>
                                <SimpleFilterSelect
                                    stations={stations}
                                    stationType={stationType}
                                    onChange={(results, newFilter, newFilterParams) => {
                                        setFilterResults(results)
                                        setFilter(newFilter)
                                        setFilterParameters(newFilterParams)
                                    }}
                                    isValidateOpen={isValidateOpen}
                                    launchSearch={(results, newFilter) => {
                                        onValidate({
                                            searchValue,
                                            filter: newFilter,
                                            filterResults: results,
                                            network,
                                            referentIds,
                                            operatorIds,
                                        })
                                    }}
                                    setIsOpenValidate={v => setIsOpenValidate(v)}
                                />
                            </Grid>
                            <Grid size={1}>
                                {accountUser.isAdmin === '1' && (
                                    <Icon className='clickable' id='add_filter_icon' onClick={openPopin} fontSize='24px'>
                                        {hasValue(filter) && filter !== FILTER_TYPE.BOOKMARK && filter !== FILTER_TYPE.NOFILTER ? 'edit' : 'note_add'}
                                    </Icon>
                                )}
                                <FilterInsertModal
                                    setOpen={setOpen}
                                    open={isOpen}
                                    selectedFilter={filter}
                                    module={MODULE}
                                    isNew={!hasValue(filter) || filter === FILTER_TYPE.BOOKMARK || filter === FILTER_TYPE.NOFILTER}
                                    onChange={setFilter}
                                />
                            </Grid>
                        </Grid>
                        {!!referentsList.length && (
                            <Grid size={3}>
                                <MultiContributorsAutocomplete
                                    label={i18n.administrator}
                                    options={referentsList}
                                    onChange={setReferentIds}
                                    keyValue='code'
                                    multiple
                                />
                            </Grid>
                        )}
                        {!!operatorsList.length && (
                            <Grid size={3}>
                                <MultiContributorsAutocomplete
                                    label={i18n.operator}
                                    options={operatorsList}
                                    onChange={setOperatorIds}
                                    keyValue='code'
                                    multiple
                                />
                            </Grid>
                        )}
                        {!!networklist.length && (
                            <Grid size={3}>
                                <SuperMultiAutocomplete
                                    label={i18n.network}
                                    options={networklist}
                                    keyLabel={showSandreCode ? 'labelWithSandre' : 'label'}
                                    additionalKeysFilter={['sandreCode', 'name', 'mnemonic']}
                                    onChange={setNetwork}
                                    values={network}
                                />
                            </Grid>
                        )}
                        <Grid size='grow' container justifyContent='flex-end' alignItems='flex-end'>
                            <Grid>
                                <Button
                                    variant='contained'
                                    fullWidth
                                    onClick={() => onLaunchSearch()}
                                >
                                    {i18n.search}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

StationFilterFields.propTypes = {
    defaultFilter: PropTypes.shape({
        filter: PropTypes.number,
        network: PropTypes.number,
        referentIds: PropTypes.arrayOf(PropTypes.number),
        operatorIds: PropTypes.arrayOf(PropTypes.number),
        searchValue: PropTypes.string,
    }),
    onValidate: PropTypes.func,
    stationType: PropTypes.string,
    module: PropTypes.string,
    stations: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(DtoStation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometerListSpecific)),
    ]),
    networklist: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(NetworkDto)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoNetwork)),
    ]),
    contributorLinks: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(DtoContributorLink),
        PropTypes.instanceOf(DtoPiezometerContributorLink),
    ])),
}

export default StationFilterFields
